/**
 * Custom hook for draft comments search with React Query caching
 * Used by Draft Review Mode in SearchPage
 */
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import { DraftCommentsSearchResponse } from '../types';

interface DraftCommentsOptions {
  edit_type?: string;
  outcome_status?: string;
  author?: string;
  drug_id?: number;
  set_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

/**
 * Hook for fetching draft comments for Draft Review Mode.
 *
 * Cache behavior:
 * - Results are cached for 2 minutes (staleTime) - shorter than normal search
 *   since outcomes can change frequently
 * - Inactive cache entries persist for 10 minutes (gcTime)
 */
export function useDraftCommentsQuery(
  options: DraftCommentsOptions = {},
  enabled: boolean = true
) {
  return useQuery<DraftCommentsSearchResponse>({
    queryKey: ['draft-comments', options],
    queryFn: async () => {
      const response = await authApi.searchDraftComments({
        ...options,
        limit: options.limit ?? 100, // Default higher limit for review mode
      });
      return response;
    },
    enabled,
    // Keep data fresh for 2 minutes (shorter due to outcome updates)
    staleTime: 2 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export default useDraftCommentsQuery;
